// OWNER_PIN_v1 (23.09.2026) -- Inhaber-PIN: EINE Stelle fuer alle PIN-Bausteine der Oberflaeche.
// Die meisten Salons arbeiten am Salon-Geraet unter dem Inhaber-Login; die PIN ist das, was nur der
// Inhaber kennt. Pro fragt sie nur bei heiklen Aktionen (zuerst: mehr Urlaub als der Anspruch,
// Urlaubsanspruch aendern -- VACATION_ENTITLEMENT_v1). Ohne vergebene PIN wird nichts gesperrt.
//   * OwnerPinPrompt -- Abfrage-Fenster (liegt ueber anderen Fenstern, z. B. dem Urlaubs-Dialog)
//   * OwnerPinCard   -- Einstellungen: festlegen, aendern, „PIN vergessen?"
//   * PinResetGate   -- Link aus der Mail (?pinreset=...): neue PIN festlegen, auch ohne Anmeldung
// Die Pruefung selbst macht immer der Server (services/owner_pin.py); hier wird nichts entschieden.
import { useEffect, useState } from "./vendor.js";
import { api } from "./api.js";
import { COLORS } from "./config.jsx";
import { Btn, Card } from "./ui.jsx";

const PIN_OK = (p) => /^\d{4,6}$/.test(String(p || ""));
const onlyDigits = (v) => String(v || "").replace(/\D/g, "").slice(0, 6);
const pinInputStyle = {
  width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${COLORS.border}`, fontSize: 20,
  letterSpacing: 8, textAlign: "center", boxSizing: "border-box", background: "var(--card)", color: COLORS.text,
};

// Text zu einer Server-Antwort mit code pin_* (409/403) -- der Server liefert den Text schon mit.
export const ownerPinErrorText = (res) =>
  (res && res.data && res.data.error) || "Die Inhaber-PIN konnte nicht geprüft werden.";

const ForgotLink = () => {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const forgot = async () => {
    setBusy(true);
    const r = await api.forgotOwnerPin();
    setBusy(false);
    if (r && r.ok) {
      setMsg(r.data && r.data.sent
        ? `Link an ${r.data.email_hint} geschickt. Er ist 1 Stunde gültig.`
        : ((r.data && r.data.message) || "Ein Link wurde gerade erst verschickt."));
    } else {
      setMsg((r && r.data && r.data.error) || "Der Link konnte nicht verschickt werden.");
    }
  };
  return (
    <div style={{ marginTop: 12, fontSize: 12.5 }}>
      <button type="button" onClick={forgot} disabled={busy}
        style={{ background: "none", border: "none", padding: 0, color: COLORS.primary, cursor: "pointer", fontSize: 12.5, fontWeight: 600, textDecoration: "underline" }}>
        {busy ? "Sende Link …" : "PIN vergessen?"}
      </button>
      {msg && <div style={{ marginTop: 6, color: COLORS.textLight, lineHeight: 1.5 }}>{msg}</div>}
    </div>
  );
};

export const OwnerPinPrompt = ({ open, title, text, error, busy, onSubmit, onCancel }) => {
  const [pin, setPin] = useState("");
  useEffect(() => { if (open) setPin(""); }, [open]);
  if (!open) return null;
  const submit = () => { if (PIN_OK(pin) && !busy) onSubmit(pin); };
  return (
    <div data-feature="OWNER_PIN_v1"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 10050, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
      <div style={{ background: "var(--card)", borderRadius: 16, padding: 22, width: "100%", maxWidth: 360, boxShadow: "0 25px 50px rgba(0,0,0,0.3)" }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 16, fontWeight: 700 }}>{title || "Inhaber-PIN"}</h3>
        {text && <p style={{ margin: "0 0 14px", fontSize: 13, color: COLORS.textLight, lineHeight: 1.55 }}>{text}</p>}
        <input type="password" inputMode="numeric" autoComplete="off" autoFocus maxLength={6} placeholder="PIN"
          value={pin} onChange={e => setPin(onlyDigits(e.target.value))}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
          style={pinInputStyle} />
        {error && <div style={{ marginTop: 8, fontSize: 12.5, color: COLORS.danger, lineHeight: 1.5 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
          <Btn variant="secondary" onClick={onCancel} disabled={busy}>Abbrechen</Btn>
          <Btn onClick={submit} disabled={busy || !PIN_OK(pin)}>{busy ? "Prüfe …" : "Freigeben"}</Btn>
        </div>
        <ForgotLink />
      </div>
    </div>
  );
};

export const OwnerPinCard = () => {
  const [st, setSt] = useState(null);          // null = laedt, false = kein Inhaber (Karte ausblenden)
  const [mode, setMode] = useState(null);      // null | "form"
  const [cur, setCur] = useState("");
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    api.getOwnerPin().then(r => { if (alive) setSt(r && r.ok ? r.data : false); });
    return () => { alive = false; };
  }, []);
  if (!st) return null;
  const reset = () => { setMode(null); setCur(""); setP1(""); setP2(""); setErr(""); };
  const save = async () => {
    setErr(""); setMsg("");
    if (!PIN_OK(p1)) { setErr("Die PIN muss aus 4 bis 6 Ziffern bestehen."); return; }
    if (p1 !== p2) { setErr("Die beiden Eingaben stimmen nicht überein."); return; }
    if (st.pin_set && !PIN_OK(cur)) { setErr("Bitte zuerst die bisherige PIN eingeben."); return; }
    setBusy(true);
    const r = await api.setOwnerPin(p1, st.pin_set ? cur : undefined);
    setBusy(false);
    if (!r || !r.ok) { setErr(ownerPinErrorText(r)); return; }
    const wasSet = st.pin_set;
    setSt(r.data);
    reset();
    setMsg(wasSet ? "Inhaber-PIN geändert." : "Inhaber-PIN festgelegt.");
  };
  const lbl = { fontSize: 12, fontWeight: 600, color: COLORS.textLight, display: "block", margin: "10px 0 4px" };
  return (
    <Card>
      <div data-feature="OWNER_PIN_v1">
        <h3 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 700 }}>Inhaber-PIN</h3>
        <p style={{ margin: "0 0 10px", fontSize: 13, color: COLORS.textLight, lineHeight: 1.55 }}>
          Die Inhaber-PIN kennst nur du. Pro fragt danach, wenn jemand mehr Urlaub eintragen will, als der Anspruch
          hergibt, oder einen Urlaubsanspruch ändert. Im Alltag fragt Pro nie danach.
        </p>
        <div style={{ fontSize: 13, fontWeight: 600, color: st.pin_set ? COLORS.success : COLORS.warning }}>
          {st.pin_set ? "✓ Inhaber-PIN ist vergeben" : "Noch keine Inhaber-PIN vergeben – Urlaub über dem Anspruch wird nur angezeigt, nicht gesperrt."}
        </div>
        {st.locked_until && (
          <div style={{ marginTop: 6, fontSize: 12.5, color: COLORS.danger }}>Nach zu vielen falschen Versuchen kurz gesperrt.</div>
        )}
        {msg && <div style={{ marginTop: 8, fontSize: 12.5, color: COLORS.success }}>{msg}</div>}
        {mode !== "form" ? (
          <div style={{ marginTop: 12 }}>
            <Btn size="sm" onClick={() => { setMsg(""); setMode("form"); }}>{st.pin_set ? "PIN ändern" : "PIN festlegen"}</Btn>
          </div>
        ) : (
          <div style={{ marginTop: 6, maxWidth: 260 }}>
            {st.pin_set && (<>
              <label style={lbl}>Bisherige PIN</label>
              <input type="password" inputMode="numeric" autoComplete="off" maxLength={6} value={cur}
                onChange={e => setCur(onlyDigits(e.target.value))} style={pinInputStyle} />
            </>)}
            <label style={lbl}>{st.pin_set ? "Neue PIN" : "PIN"} (4–6 Ziffern)</label>
            <input type="password" inputMode="numeric" autoComplete="off" maxLength={6} value={p1}
              onChange={e => setP1(onlyDigits(e.target.value))} style={pinInputStyle} />
            <label style={lbl}>PIN wiederholen</label>
            <input type="password" inputMode="numeric" autoComplete="off" maxLength={6} value={p2}
              onChange={e => setP2(onlyDigits(e.target.value))}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); save(); } }} style={pinInputStyle} />
            <div style={{ marginTop: 6, fontSize: 11.5, color: COLORS.textLight }}>Keine leicht zu ratende PIN wie 1234 oder 0000.</div>
            {err && <div style={{ marginTop: 8, fontSize: 12.5, color: COLORS.danger }}>{err}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <Btn size="sm" variant="secondary" onClick={reset} disabled={busy}>Abbrechen</Btn>
              <Btn size="sm" onClick={save} disabled={busy}>{busy ? "Speichere …" : "Speichern"}</Btn>
            </div>
          </div>
        )}
        {st.pin_set && <ForgotLink />}
      </div>
    </Card>
  );
};

export const PinResetGate = () => {
  const [token] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("pinreset") || null; } catch (e) { return null; }
  });
  const [hidden, setHidden] = useState(false);
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!token || hidden) return null;
  const close = () => {
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete("pinreset");
      window.history.replaceState(null, "", u.pathname + u.search + u.hash);
    } catch (e) { /* egal */ }
    setHidden(true);
  };
  const save = async () => {
    setErr("");
    if (!PIN_OK(p1)) { setErr("Die PIN muss aus 4 bis 6 Ziffern bestehen."); return; }
    if (p1 !== p2) { setErr("Die beiden Eingaben stimmen nicht überein."); return; }
    setBusy(true);
    const r = await api.resetOwnerPin(token, p1);
    setBusy(false);
    if (!r || !r.ok) { setErr((r && r.data && r.data.error) || "Die PIN konnte nicht gespeichert werden."); return; }
    setDone(true);
  };
  return (
    <div data-feature="OWNER_PIN_v1" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 10060, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "var(--card)", borderRadius: 16, padding: 22, width: "100%", maxWidth: 360, boxShadow: "0 25px 50px rgba(0,0,0,0.3)" }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 16, fontWeight: 700 }}>Neue Inhaber-PIN festlegen</h3>
        {done ? (<>
          <p style={{ margin: "0 0 14px", fontSize: 13, color: COLORS.success, lineHeight: 1.55 }}>Die neue Inhaber-PIN ist gespeichert.</p>
          <div style={{ display: "flex", justifyContent: "flex-end" }}><Btn onClick={close}>Schließen</Btn></div>
        </>) : (<>
          <p style={{ margin: "0 0 12px", fontSize: 13, color: COLORS.textLight, lineHeight: 1.55 }}>4 bis 6 Ziffern, keine leicht zu ratende PIN wie 1234 oder 0000.</p>
          <input type="password" inputMode="numeric" autoComplete="off" autoFocus maxLength={6} placeholder="Neue PIN"
            value={p1} onChange={e => setP1(onlyDigits(e.target.value))} style={pinInputStyle} />
          <input type="password" inputMode="numeric" autoComplete="off" maxLength={6} placeholder="PIN wiederholen"
            value={p2} onChange={e => setP2(onlyDigits(e.target.value))}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); save(); } }}
            style={{ ...pinInputStyle, marginTop: 10 }} />
          {err && <div style={{ marginTop: 8, fontSize: 12.5, color: COLORS.danger, lineHeight: 1.5 }}>{err}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
            <Btn variant="secondary" onClick={close} disabled={busy}>Abbrechen</Btn>
            <Btn onClick={save} disabled={busy}>{busy ? "Speichere …" : "PIN speichern"}</Btn>
          </div>
        </>)}
      </div>
    </div>
  );
};
